/*
  EmailCapture — explicit-consent Hostinger Reach signup.
  The form stays fully on-brand; the server forwards only validated contact data
  to Reach, where double opt-in, suppression, campaigns and unsubscribe are
  managed. A successful submit always means "check your inbox", never an
  immediate subscription claim.
*/
import { useRef, useState, FormEvent } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { CAPTURE_READY, CONTACT_EMAIL } from "@/lib/brand";
import { submitSubscribe } from "@/lib/capture";
import { useI18n } from "@/lib/i18n";
import { useUrlSrc } from "@/lib/attribution";

interface Props {
  variant?: "stack" | "inline";
  cta?: string;
  source?: string;
  headline?: string;
  note?: string;
}

function fallbackIntent(source: string, label: string, urlSrc?: string) {
  const s = source.toLowerCase();
  const srcLine = urlSrc ? `\nOriginating article: ${urlSrc}` : "";
  if (s.includes("vault")) {
    return {
      subject: "BEARTH Vault — founder price waitlist",
      body: `Karibu!\n\nPlease add me to the BEARTH Vault founder-price waitlist.\n\nCTA: ${label}\nSource: ${source}${srcLine}`,
    };
  }
  if (s.includes("audit")) {
    return {
      subject: "BEARTH Audit — send my next steps",
      body: `Karibu!\n\nPlease send me the next steps for my BEARTH income exposure audit result.\n\nCTA: ${label}\nSource: ${source}${srcLine}`,
    };
  }
  return {
    subject: "SIGNAL — add me to the list",
    body: `Karibu!\n\nPlease add me to the BEARTH SIGNAL email list.\n\nCTA: ${label}\nSource: ${source}${srcLine}`,
  };
}

export default function EmailCapture({ variant = "stack", cta, source = "site", headline, note }: Props) {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "pending">("idle");
  const mountedAt = useRef(Date.now());
  const label = cta ?? t.email.cta;
  const captureHeadline = headline ?? t.email.label;
  const captureNote = note ?? t.email.note;
  const urlSrc = useUrlSrc();
  const fallback = fallbackIntent(source, label, urlSrc);
  const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(fallback.subject)}&body=${encodeURIComponent(fallback.body)}`;

  if (!CAPTURE_READY) {
    if (variant === "inline") {
      return (
        <a href={mailtoHref} className="cta press w-full sm:w-auto">
          {label} <ArrowRight className="w-3.5 h-3.5" />
        </a>
      );
    }
    return (
      <div className="signature-panel grid gap-3 w-full max-w-md p-5">
        <span className="kicker">{captureHeadline}</span>
        <a href={mailtoHref} className="cta press">
          {label} <ArrowRight className="w-4 h-4" />
        </a>
        <p className="text-[10px] text-[rgba(243,234,225,0.62)] tracking-wide">{captureNote}</p>
      </div>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error(t.email.invalid);
      return;
    }
    if (!consent) {
      toast.error(t.email.consentRequired);
      return;
    }
    setState("sending");
    const result = await submitSubscribe({
      email,
      language: lang,
      source,
      ...(urlSrc ? { src: urlSrc } : {}),
      consent: true,
      hp: honeypot,
      elapsed_ms: Date.now() - mountedAt.current,
    });
    if (result === "pending") {
      setState("pending");
      toast.success(t.email.success);
      setEmail("");
      setConsent(false);
      setHoneypot("");
      mountedAt.current = Date.now();
    } else {
      setState("idle");
      toast.error(result === "unavailable" ? t.email.unavailable(CONTACT_EMAIL) : t.email.fail(CONTACT_EMAIL));
    }
  }

  const buttonLabel = state === "pending" ? t.email.done : state === "sending" ? t.email.sending : label;
  const hiddenTrap = (
    <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
      <label htmlFor={`company-${source}`}>Company</label>
      <input
        id={`company-${source}`}
        name="company"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />
    </div>
  );
  const consentRow = (
    <label className="flex items-start gap-2.5 text-[11px] leading-[1.6] text-[rgba(243,234,225,0.66)] cursor-pointer">
      <input
        type="checkbox"
        checked={consent}
        onChange={(e) => setConsent(e.target.checked)}
        className="mt-[2px] shrink-0 accent-[#F5C400]"
        data-testid="subscribe-consent"
      />
      <span>
        {t.email.consent}{" "}
        <Link href="/legal/privacy" className="underline underline-offset-2 decoration-[rgba(245,196,0,.5)] hover:text-foreground">
          {t.email.privacyLink}
        </Link>
      </span>
    </label>
  );

  if (variant === "inline") {
    return (
      <form onSubmit={onSubmit} className="relative grid gap-2.5 w-full" data-subscribe-source={source}>
        {hiddenTrap}
        <div className="flex flex-col sm:flex-row gap-2 w-full">
          <label className="sr-only" htmlFor={`email-${source}`}>Email</label>
          <input
            id={`email-${source}`}
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.email.placeholder}
            className="field flex-1"
            autoComplete="email"
          />
          <button type="submit" disabled={state === "sending"} className="cta press whitespace-nowrap">
            {buttonLabel} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {consentRow}
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit} className="signature-panel relative grid gap-3 w-full max-w-md p-5" data-subscribe-source={source}>
      {hiddenTrap}
      <label className="kicker" htmlFor={`email-${source}`}>{captureHeadline}</label>
      <input
        id={`email-${source}`}
        required
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t.email.placeholder}
        className="field"
        autoComplete="email"
      />
      {consentRow}
      <button type="submit" disabled={state === "sending"} className="cta press">
        {buttonLabel} <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-[10px] text-[rgba(243,234,225,0.62)] tracking-wide">{captureNote}</p>
    </form>
  );
}
